/**
 * Phase 8 T-15 §14 C–D — Step 1: "Let's get to know you"
 *   /onboarding/start
 *
 * Fields: first name, last name, email (readonly), email-notifications toggle.
 * Footer: Back (disabled / hidden on step 1) | Continue
 * No Skip on step 1 (per §14 E spec).
 */

import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ArrowLeft, ArrowRight, User } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { orgService } from '@core/supabase/services/orgService';
import type { OnboardingOutletContext } from './OnboardingLayout';

export function OnboardingStartStep() {
  const { navigate } = useOutletContext<OnboardingOutletContext>();
  const { user } = useAuth();

  const nameParts = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);
  const [firstName, setFirstName] = useState(nameParts[0] ?? '');
  const [lastName, setLastName]   = useState(nameParts.slice(1).join(' ') ?? '');
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      /* Persist display_name to org_users row if org exists. TODO(§8-v2): dedicated user profile endpoint */
      if (user?.orgId && user?.id) {
        const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ') || user.name;
        await orgService.upsertOrgUser({
          org_id: user.orgId,
          keycloak_sub: user.id,
          display_name: displayName,
          email: user.email,
          role: user.roles[0] ?? 'owner',
          is_active: true,
          joined_at: new Date().toISOString(),
        });
      }
      /* emailNotifs preference — TODO(§8-v2): persist to user_preferences table */
      localStorage.setItem('onb_email_notifs', String(emailNotifs));
    } catch (_) {
      /* non-blocking — proceed regardless */
    } finally {
      setSaving(false);
      navigate('/onboarding/workspace');
    }
  };

  return (
    <div className="flex flex-col min-h-[480px]">

      {/* Header */}
      <div className="px-7 pt-7 pb-5 border-b border-[var(--vscode-border-primary)]">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'rgba(139,128,249,0.12)', color: '#8B80F9' }}>
            <User className="w-4 h-4" />
          </div>
          <h2 className="text-[16px] font-bold vscode-text-primary">Let&apos;s get to know you</h2>
        </div>
        <p className="text-[12px] vscode-text-tertiary ml-11">Step 1 of 3 — Personal details</p>
      </div>

      {/* Body */}
      <div className="flex-1 px-7 py-5 space-y-4 overflow-y-auto">

        {/* First + Last name */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[12px] font-medium vscode-text-secondary block mb-1" htmlFor="onb-first-name">
              First name
            </label>
            <input
              id="onb-first-name"
              className="input text-sm"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ada"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[12px] font-medium vscode-text-secondary block mb-1" htmlFor="onb-last-name">
              Last name
            </label>
            <input
              id="onb-last-name"
              className="input text-sm"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Lovelace"
            />
          </div>
        </div>

        {/* Email (readonly) */}
        <div>
          <label className="text-[12px] font-medium vscode-text-secondary block mb-1" htmlFor="onb-email">
            Email <span className="vscode-text-tertiary font-normal">(read-only)</span>
          </label>
          <input
            id="onb-email"
            className="input text-sm opacity-60 cursor-not-allowed"
            value={user?.email ?? ''}
            readOnly
            tabIndex={-1}
          />
          <p className="text-[11px] vscode-text-tertiary mt-1">
            Your sign-in email. Change it from your account settings.
          </p>
        </div>

        {/* Email notifications toggle */}
        <label className="flex items-center justify-between gap-4 rounded-[10px] border border-[var(--vscode-border-primary)] px-4 py-3 cursor-pointer hover:vscode-bg-hover transition-colors">
          <div>
            <p className="text-[13px] font-semibold vscode-text-primary">Email notifications</p>
            <p className="text-[11px] vscode-text-tertiary mt-0.5">
              Receive pipeline updates, interview reminders, and digest emails.
            </p>
          </div>
          <div
            role="switch"
            aria-checked={emailNotifs}
            onClick={() => setEmailNotifs((v) => !v)}
            className={[
              'relative w-10 h-6 rounded-full transition-colors shrink-0 cursor-pointer',
              emailNotifs ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform',
                emailNotifs ? 'translate-x-5' : 'translate-x-1',
              ].join(' ')}
            />
          </div>
        </label>

      </div>

      {/* Footer */}
      <div className="px-7 py-4 border-t border-[var(--vscode-border-primary)] flex items-center gap-3">
        {/* Spacer where Back would be — step 1 has no Back */}
        <div className="w-[72px] shrink-0" />
        <div className="flex-1" />
        <button
          type="button"
          disabled={saving}
          onClick={() => void handleContinue()}
          className="flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-60"
          style={{
            background: 'linear-gradient(135deg, #7B6EE8 0%, #8B80F9 100%)',
            boxShadow: '0 3px 12px rgba(139,128,249,0.38)',
          }}
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>

    </div>
  );
}
